import React, { memo, useCallback, useMemo } from 'react';
import { shallowEqual, useSelector } from 'react-redux';

import { Box, Typography } from '@mui/material';
import PropTypes from 'prop-types';

import { selectStepStatusesByRun } from 'store/aiProcessRuns';

import {
  formatLiveRunHeadline,
  formatRelativeTime,
  getRunLiveCounts,
  getRunStepCounts,
  humanizeTrigger,
} from '../../../AiActivity.utils';
import ProcessColorChip from '../../../ProcessColorChip';
import StepMiniBars from '../../../StepMiniBars';
import {
  dotSeparatorStyles,
  liveDotStyles,
  rowBodyStyles,
  rowLeadingStyles,
  rowMetaStyles,
  rowStyles,
  rowTitleRowStyles,
  rowTitleTextStyles,
  rowTrailingStyles,
} from '../../LiveCard.styles';

const LiveCardRow = ({ run, processName, onSelectExecution }) => {
  const stepStatuses = useSelector(
    useMemo(() => selectStepStatusesByRun(run.id), [run.id]),
    shallowEqual,
  );
  const { total, finished: completed } = getRunStepCounts(run, stepStatuses);
  const { runningCount, isNeedsInput } = getRunLiveCounts({ run, steps: [] });

  const onClick = useCallback(() => onSelectExecution(run.id), [onSelectExecution, run.id]);

  return (
    <Box sx={rowStyles} onClick={onClick}>
      <Box sx={rowLeadingStyles}>
        <Box sx={liveDotStyles} />
        <Box sx={rowBodyStyles}>
          <Box sx={rowTitleRowStyles}>
            <ProcessColorChip name={processName} processId={run.aiProcessId} />
            <Typography sx={rowTitleTextStyles} variant="medium14">
              {formatLiveRunHeadline({ completed, total, runningCount, isNeedsInput })}
            </Typography>
          </Box>
          <Box sx={rowMetaStyles}>
            <Typography variant="regular12">{humanizeTrigger(run)}</Typography>
            <Box sx={dotSeparatorStyles} />
            <Typography variant="regular12">started {formatRelativeTime(run.startedAt)}</Typography>
          </Box>
        </Box>
      </Box>
      <Box sx={rowTrailingStyles}>
        <StepMiniBars
          completedSteps={completed}
          status={run.status}
          stepStatuses={stepStatuses}
          totalSteps={total}
        />
      </Box>
    </Box>
  );
};

LiveCardRow.propTypes = {
  processName: PropTypes.string.isRequired,
  run: PropTypes.object.isRequired,
  onSelectExecution: PropTypes.func.isRequired,
};

export default memo(LiveCardRow);
